import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../../Layout";
import apiService from "../../services/service";
import API_ROUTES_V2 from "../../api-routes-v2";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Card } from "primereact/card";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { formatTenantType } from "../../utils/tenantTypeFormatter";
import "./TenantDetails.scss";
import TenantChildEntity from "./components/TenantChildEntity";

const TenantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toastRef = useRef(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTenantDetails();
  }, [id]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      const url = API_ROUTES_V2.FETCH_TENANT.replace("{id}", id);
      console.log("Fetching tenant from:", url);

      const response = await apiService(url, "GET");
      console.log("Tenant response received:", response);

      const tenantData = response?.data || response;
      setTenant(tenantData);
    } catch (error) {
      console.error("Error fetching tenant:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch tenant details",
        life: 3000,
      });
      // Redirect back to tenants list after error
      setTimeout(() => navigate("/tenants"), 2000);
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (status) => {
    const statusValue = status || "ACTIVE";
    switch (statusValue) {
      case "ACTIVE":
        return (
          <span className="tenant-badge tenant-badge--active">Active</span>
        );
      case "INACTIVE":
        return (
          <span className="tenant-badge tenant-badge--inactive">
            Not Active
          </span>
        );
      default:
        return "-";
    }
  };

  const renderCountry = (country) => {
    if (!country) return "-";
    return (
      <div className="flex align-items-center gap-2">
        <ReactCountryFlag
          countryCode={country}
          svg
          style={{ width: "20px", height: "20px" }}
        />
        <span>{getName(country) || country}</span>
      </div>
    );
  };

  const renderAccountManager = (manager) => {
    if (!manager) return "-";
    // If preferred_name is present, show only that; otherwise show given_name + surname
    let name;
    if (manager.preferred_name) {
      name = manager.preferred_name;
    } else {
      const givenName = manager.given_name || "";
      const surname = manager.surname || "";
      name = `${givenName} ${surname}`.trim() || "-";
    }
    return name;
  };

  const getBreadcrumbLabel = () => {
    if (!tenant) return "Loading...";
    const countryName = tenant.country ? getName(tenant.country) : "";
    const countryFlag = tenant.country ? (
      <ReactCountryFlag
        countryCode={tenant.country}
        svg
        style={{ width: "16px", height: "16px", marginRight: "4px" }}
      />
    ) : null;

    return (
      <span>
        {tenant.tenant_name}
        {countryName && (
          <span style={{ marginLeft: "8px" }}>
            ({countryFlag}
            {countryName})
          </span>
        )}
      </span>
    );
  };

  const getTenantInitials = (name) => {
    if (!name) return "T";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCoreTenantInfo = () => {
    navigate(`/tenants/${id}/info`);
  };

  return (
    <Layout
      crumbs={[
        { label: "Tenants", url: "/tenants" },
        {
          label: loading ? (
            <Skeleton width="200px" height="20px" className="mr-1" />
          ) : (
            getBreadcrumbLabel()
          ),
        },
      ]}
    >
      <Toast ref={toastRef} />
      <div className="tenant-details__page">
        {loading ? (
          <Card className="gen-card h-full w-full">
            <div className="tenant-details__skeleton">
              <Skeleton width="30%" height="40px" className="mb-3" />
              <Skeleton width="100%" height="20px" className="mb-2" />
              <Skeleton width="100%" height="20px" className="mb-2" />
              <Skeleton width="100%" height="20px" className="mb-2" />
              <Skeleton width="100%" height="20px" />
            </div>
          </Card>
        ) : tenant ? (
          <Card className="gen-card h-full w-full">
            <div className="tenant-details__container">
              {/* Header Section */}
              <div className="tenant-details__header-section">
                <div className="tenant-details__header-left">
                  <div className="tenant-avatar">
                    {getTenantInitials(tenant.tenant_name)}
                    {/* <button
                      className="tenant-avatar__edit-btn"
                      title="Edit tenant"
                    >
                      <i className="pi pi-pencil"></i>
                    </button> */}
                  </div>
                  <div className="tenant-details__header-info">
                    <p className="tenant-details__label-small">Tenant</p>
                    <div className="tenant-details__name-badge">
                      <h2 className="tenant-details__title">
                        {tenant.tenant_name}
                      </h2>
                      {renderStatus(tenant.status)}
                    </div>
                  </div>
                </div>
                <button
                  className="btn-core-tenant-info"
                  onClick={handleCoreTenantInfo}
                >
                  Core Tenant Information
                </button>
              </div>

              {/* Info Row */}
              <div className="tenant-details__info-row">
                <div className="tenant-details__info-item">
                  <label className="tenant-details__info-label">Country:</label>
                  <p className="tenant-details__info-value">
                    {renderCountry(tenant.country)}
                  </p>
                </div>

                <div className="tenant-details__info-item">
                  <label className="tenant-details__info-label">Type:</label>
                  <p className="tenant-details__info-value">
                    {formatTenantType(tenant.type) || "-"}
                  </p>
                </div>

                <div className="tenant-details__info-item">
                  <label className="tenant-details__info-label">
                    Salesforce ID:
                  </label>
                  <p className="tenant-details__info-value">
                    {tenant.salesforce_id || "-"}
                  </p>
                </div>

                <div className="tenant-details__info-item">
                  <label className="tenant-details__info-label">
                    Account Name:
                  </label>
                  <p className="tenant-details__info-value">
                    {tenant.account_name || "-"}
                  </p>
                </div>

                <div className="tenant-details__info-item">
                  <label className="tenant-details__info-label">
                    Account Manager:
                  </label>
                  <Link
                    to={`account-manager/${tenant.account_manager.id}`}
                    className="manager-link "
                  >
                    <p className="tenant-details__info-value account-manager-link">
                      {renderAccountManager(tenant.account_manager)}
                    </p>
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="gen-card h-full w-full">
            <div className="tenant-details__error">
              <p>Tenant not found</p>
            </div>
          </Card>
        )}
        <Card className="gen-card h-full w-full child__entity mt-5">
          <p className="child__entity--heading mb-2">Child Entities</p>
          <p className="child__entity--description mb-5 mt-0">
            Find all entities listed under this parent company
          </p>
          <TenantChildEntity />
        </Card>
      </div>
    </Layout>
  );
};

export default TenantDetails;
